// src/mobile_pages/contract/Info_view.jsx
// 2025 모바일 UI/UX 개선

import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getContractDetail, updateContract } from "../../api/contract/info";
import SidePanel from "../../components/common/layout/SidePanel";
import MobileAttachmentBox from "../../components/common/papers/MobileAttachmentBox";
import MobileShareButton from "../../components/common/mobile/MobileShareButton";
import FileList from "../../components/common/papers/FileList";
import { softDeleteDoc } from "../../api/papers";
import { formatDate } from "../../utils/date";

const emptyMeta = {
  revision: 1,
  version: 1,
  is_current: true,
  contract_code: "",
  confirmer_company_name: "",
  contract_type: "",
  construction_type: "",
  amount: "",
  signed_date_actual: "",
  start_date_actual: "",
  end_date_actual: "",
  sign_date_expected: "",
  end_date_expected: "",
  memo: "",
};

const normalize = (data) => ({
  dns: data?.dns ?? "",
  title: data?.title ?? "",
  created_at: data?.created_at ?? null,
  created_by: data?.created_by ?? "",
  versions: data?.versions ?? [],
  meta: { ...emptyMeta, ...(data?.meta ?? {}) },
});

const Info_view = ({ contractId: propContractId, onDeleted, onUpdated, onClose }) => {
  const { contractId: routeContractId } = useParams();
  const navigate = useNavigate();
  const contractId = propContractId || routeContractId;

  const [contract, setContract] = useState(() => normalize({}));
  const [form, setForm] = useState(() => normalize({}));
  const [loading, setLoading] = useState(true);
  const [errMsg, setErrMsg] = useState("");
  const [editing, setEditing] = useState(false);

  const [loadedFiles, setLoadedFiles] = useState([]);
  const [deleting, setDeleting] = useState(false);
  const [showVersionPanel, setShowVersionPanel] = useState(false);

  const attachmentRef = useRef(null);

  const fetchDetail = useCallback(async (id = contractId) => {
    if (!id) return;
    setLoading(true);
    setErrMsg("");
    try {
      const data = await getContractDetail(id);
      const norm = normalize(data);
      setContract(norm);
      setForm(norm);
    } catch (e) {
      console.error("계약 상세 불러오기 실패:", e);
      setErrMsg("계약 정보를 불러오지 못했습니다.");
    } finally {
      setLoading(false);
    }
  }, [contractId]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  const handleTopChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleMetaChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      meta: {
        ...prev.meta,
        [name]: name === "amount" ? value.replace(/[^0-9]/g, "") : value,
      },
    }));
  };

  const handleSave = async () => {
    if (!contractId) return;
    if (!form.title) {
      alert("계약명을 입력해 주세요.");
      return;
    }

    try {
      const fd = new FormData();
      fd.append("contract_id", contractId);
      fd.append("title", form.title);
      fd.append("contract_type", form.meta.contract_type);
      fd.append("contract_code", form.meta.contract_code);
      fd.append("confirmer_company_name", form.meta.confirmer_company_name);
      fd.append("amount", form.meta.amount);
      fd.append("signed_date_actual", form.meta.signed_date_actual);
      fd.append("start_date_actual", form.meta.start_date_actual);
      fd.append("end_date_actual", form.meta.end_date_actual);
      fd.append("sign_date_expected", form.meta.sign_date_expected);
      fd.append("end_date_expected", form.meta.end_date_expected);
      fd.append("memo", form.meta.memo);

      const res = await updateContract(contractId, fd);
      const newDns = res?.dns ?? contractId;
      await attachmentRef.current?.save(newDns);

      alert("수정되었습니다");
      setEditing(false);
      await fetchDetail(newDns);
      onUpdated?.(newDns);
    } catch (err) {
      console.error("수정 실패:", err);
      alert("수정 실패 다시 시도해주세요");
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("해당 계약과 모든 버전 문서가 삭제됩니다. 정말 삭제하시겠습니까?")) {
      return;
    }

    try {
      setDeleting(true);
      const dnsList = [contract.dns];
      if (Array.isArray(contract.versions) && contract.versions.length > 0) {
        dnsList.push(...contract.versions.map((v) => v.dns));
      }

      await Promise.all(dnsList.map((dns) => softDeleteDoc(dns, true)));
      alert("계약과 모든 버전 문서가 삭제되었습니다.");
      onDeleted?.();
      onClose?.();
      if (!propContractId) navigate(-1);
    } catch (err) {
      console.error("삭제 실패:", err);
      alert(`삭제 실패: ${err?.message || "알 수 없는 오류"}`);
    } finally {
      setDeleting(false);
    }
  };

  const headerMeta = contract.meta;
  const versions = useMemo(() => contract.versions ?? [], [contract]);

  // 필드 렌더 헬퍼
  const Field = ({ label, value, icon: Icon, children, className = "" }) => (
    <div className={`space-y-1 ${className}`}>
      <div className="flex items-center gap-1.5 text-xs font-medium text-gray-500">
        {label}
      </div>
      {children ?? <div className="text-sm text-gray-900">{value || "-"}</div>}
    </div>
  );

  if (!contractId) return (
    <div className="p-6 text-center text-gray-500 text-sm">계약 ID 없음</div>
  );

  if (loading) return (
    <div className="space-y-4 animate-pulse">
      <div className="h-6 bg-gray-200 rounded w-1/2" />
      <div className="h-4 bg-gray-100 rounded w-3/4" />
      <div className="h-4 bg-gray-100 rounded w-2/3" />
      <div className="h-32 bg-gray-100 rounded" />
    </div>
  );

  if (errMsg) return (
    <div className="p-6 text-center text-red-500 text-sm">{errMsg}</div>
  );

  return (
    <div className="mobile-page-root mb-16">
      <div className="mobile-page-root-inner space-y-3">
        {/* 액션 버튼 영역 */}
        <div className="flex items-center justify-between">
          <button
            onClick={() => setShowVersionPanel(true)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 text-blue-700 rounded-lg text-xs font-medium"
          >
            수정 내역
          </button>

          <div className="flex items-center gap-1 flex-nowrap flex-shrink-0">
            <span className="text-[10px] text-gray-400 flex-shrink-0">
              Rev.{headerMeta.revision} v{headerMeta.version}
            </span>

            {editing ? (
              <>
                <button
                  onClick={handleSave}
                  className="btn-save-sm inline-flex items-center gap-1 px-2 py-1 text-[11px]"
                >
                  저장
                </button>
                <button
                  onClick={() => { setEditing(false); setForm(contract); }}
                  className="btn-cancel-sm inline-flex items-center gap-1 px-2 py-1 text-[11px]"
                >
                  취소
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => setEditing(true)}
                  className="btn-edit-sm inline-flex items-center gap-1 px-2 py-1 text-[11px]"
                >
                  수정
                </button>
                <button
                  onClick={handleDelete}
                  disabled={deleting}
                  className="btn-delete-sm inline-flex items-center gap-1 disabled:opacity-50 px-2 py-1 text-[11px]"
                >
                  {deleting ? "삭제중" : "삭제"}
                </button>
              </>
            )}
            <MobileShareButton
              title={contract?.title || "계약"}
              className="btn-basic-sm px-2 py-1 text-[11px]"
            />
          </div>
        </div>

        {/* 메인 정보 카드 */}
        <div className="rounded-lg border bg-white p-3 space-y-4">
          {/* 계약명 */}
          <div>
            <div className="text-xs text-gray-500 mb-1">계약명</div>
            {editing ? (
              <input
                type="text"
                name="title"
                value={form.title || ""}
                onChange={handleTopChange}
                className="w-full px-3 py-2 bg-white border-0 rounded-xl text-sm focus:ring-2 focus:ring-blue-500"
              />
            ) : (
              <div className="text-base font-semibold text-gray-900">{contract.title || "-"}</div>
            )}
          </div>

          {/* 요약 정보 카드들 */}
          <div className="grid grid-cols-2 gap-2">
            <div className="bg-white rounded-xl p-3">
              <div className="flex items-center gap-1.5 text-xs text-gray-500 mb-1">
                계약금액
              </div>
              {editing ? (
                <input
                  type="text"
                  name="amount"
                  value={form.meta.amount || ""}
                  onChange={handleMetaChange}
                  placeholder="금액 입력"
                  className="w-full text-sm font-semibold bg-transparent border-0 p-0 focus:ring-0"
                />
              ) : (
                <div className="text-sm font-semibold text-gray-900">
                  {contract.meta.amount ? Number(contract.meta.amount).toLocaleString("ko-KR") + "원" : "-"}
                </div>
              )}
            </div>

            <div className="bg-white rounded-xl p-3">
              <div className="flex items-center gap-1.5 text-xs text-gray-500 mb-1">
                주계약업체
              </div>
              {editing ? (
                <input
                  type="text"
                  name="confirmer_company_name"
                  value={form.meta.confirmer_company_name || ""}
                  onChange={handleMetaChange}
                  placeholder="업체명"
                  className="w-full text-sm font-semibold bg-transparent border-0 p-0 focus:ring-0"
                />
              ) : (
                <div className="text-sm font-semibold text-gray-900 truncate">
                  {contract.meta.confirmer_company_name || "-"}
                </div>
              )}
            </div>
          </div>

          {/* 상세 정보 */}
          <div className="bg-white rounded-xl p-4 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Field label="계약 구분">
                {editing ? (
                  <select
                    name="contract_type"
                    value={form.meta.contract_type || ""}
                    onChange={handleMetaChange}
                    className="w-full px-2 py-1.5 bg-gray-50 border-0 rounded-lg text-sm"
                  >
                    <option value="">선택</option>
                    <option value="지명경쟁입찰">지명경쟁입찰</option>
                    <option value="제한경쟁입찰">제한경쟁입찰</option>
                    <option value="일반경쟁입찰">일반경쟁입찰</option>
                    <option value="수의계약">수의계약</option>
                    <option value="협상계약">협상계약</option>
                    <option value="최저가낙찰">최저가낙찰</option>
                    <option value="종합평가낙찰">종합평가낙찰</option>
                    <option value="턴키">턴키</option>
                    <option value="기술제안입찰">기술제안입찰</option>
                    <option value="대안입찰">대안입찰</option>
                    <option value="단가계약">단가계약</option>
                    <option value="총액계약">총액계약</option>
                  </select>
                ) : (
                  <span className="text-sm">{contract.meta.contract_type || "-"}</span>
                )}
              </Field>

              <Field label="계약 코드">
                {editing ? (
                  <input
                    type="text"
                    name="contract_code"
                    value={form.meta.contract_code || ""}
                    onChange={handleMetaChange}
                    className="w-full px-2 py-1.5 bg-gray-50 border-0 rounded-lg text-sm"
                  />
                ) : (
                  <span className="text-sm">{contract.meta.contract_code || "-"}</span>
                )}
              </Field>

              <Field label="공사 구분">
                {editing ? (
                  <select
                    name="construction_type"
                    value={form.meta.construction_type || ""}
                    onChange={handleMetaChange}
                    className="w-full px-2 py-1.5 bg-gray-50 border-0 rounded-lg text-sm"
                  >
                    <option value="">선택</option>
                    <option value="본공사">본공사</option>
                    <option value="전기공사">전기공사</option>
                    <option value="통신공사">통신공사</option>
                    <option value="소방공사">소방공사</option>
                  </select>
                ) : (
                  <span className="text-sm">{contract.meta.construction_type || "-"}</span>
                )}
              </Field>

              <Field label="작성자" value={contract.created_by} />
            </div>

            <div className="pt-2 border-t">
              <div className="text-xs font-medium text-gray-500 mb-2">계약 기간</div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="계약일">
                  {editing ? (
                    <input type="date" name="signed_date_actual" value={form.meta.signed_date_actual || ""} onChange={handleMetaChange} className="w-full px-2 py-1.5 bg-gray-50 border-0 rounded-lg text-sm" />
                  ) : (
                    <span className="text-sm">{contract.meta.signed_date_actual ? formatDate(contract.meta.signed_date_actual) : "-"}</span>
                  )}
                </Field>
                <Field label="착수일">
                  {editing ? (
                    <input type="date" name="start_date_actual" value={form.meta.start_date_actual || ""} onChange={handleMetaChange} className="w-full px-2 py-1.5 bg-gray-50 border-0 rounded-lg text-sm" />
                  ) : (
                    <span className="text-sm">{contract.meta.start_date_actual ? formatDate(contract.meta.start_date_actual) : "-"}</span>
                  )}
                </Field>
                <Field label="종료일">
                  {editing ? (
                    <input type="date" name="end_date_actual" value={form.meta.end_date_actual || ""} onChange={handleMetaChange} className="w-full px-2 py-1.5 bg-gray-50 border-0 rounded-lg text-sm" />
                  ) : (
                    <span className="text-sm">{contract.meta.end_date_actual ? formatDate(contract.meta.end_date_actual) : "-"}</span>
                  )}
                </Field>
                <Field label="종료 예정">
                  {editing ? (
                    <input type="date" name="end_date_expected" value={form.meta.end_date_expected || ""} onChange={handleMetaChange} className="w-full px-2 py-1.5 bg-gray-50 border-0 rounded-lg text-sm" />
                  ) : (
                    <span className="text-sm">{contract.meta.end_date_expected ? formatDate(contract.meta.end_date_expected) : "-"}</span>
                  )}
                </Field>
              </div>
            </div>

            {/* 특이사항 */}
            <div className="pt-2 border-t">
              <div className="text-xs font-medium text-gray-500 mb-2">특이사항</div>
              {editing ? (
                <textarea
                  name="memo"
                  value={form.meta.memo || ""}
                  onChange={handleMetaChange}
                  className="w-full h-24 px-3 py-2 bg-gray-50 border-0 rounded-xl text-sm resize-none"
                  placeholder="특이사항을 입력하세요"
                />
              ) : (
                <div className="text-sm text-gray-700 bg-gray-50 rounded-xl p-3 min-h-[60px] whitespace-pre-line">
                  {contract.meta.memo || "-"}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* 첨부파일 */}
        <div className="bg-gray-50 rounded-2xl p-4">
          <div className="text-xs font-medium text-gray-500 mb-2">첨부파일</div>
          {editing ? (
            <MobileAttachmentBox ref={attachmentRef} initial={loadedFiles} />
          ) : (
            <FileList
              dns={contract.dns}
              onFilesLoaded={(files) => {
                const mapped = files.map((f) => ({
                  _type: "library",
                  id: f.file_id ?? f.id,
                  name: f.name,
                }));
                setLoadedFiles(mapped);
              }}
            />
          )}
        </div>

        {/* 수정 내역 패널 */}
        <SidePanel
          isOpen={showVersionPanel}
          onClose={() => setShowVersionPanel(false)}
          title="수정 내역"
          width={350}
          side="left"
        >
          <div className="space-y-2 p-2">
            {versions.length > 0 ? (
              versions.map((v, idx) => (
                <div key={idx} className="bg-gray-50 rounded-xl p-3">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-medium text-gray-700">
                      Rev.{v.revision} v{v.version}
                    </span>
                    {v.is_current && (
                      <span className="px-2 py-0.5 text-[10px] rounded-full bg-green-100 text-green-700">
                        최신
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-gray-900 truncate">{v.title}</div>
                  <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
                    <span>{v.created_by}</span>
                    <span>·</span>
                    <span>{v.created_at ? formatDate(v.created_at) : "-"}</span>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center text-sm text-gray-500 py-8">
                수정 내역이 없습니다.
              </div>
            )}
          </div>
        </SidePanel>
      </div>
    </div>
  );
};

export default Info_view;
